import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { readStorage, writeStorage } from '../lib/storage';

/**
 * A one-time callout on a visitor's first successful render, naming two
 * things Plotter already does better than Mermaid Live but that are easy to
 * miss inside a menu: pan/zoom/Fit on the preview canvas, and titled,
 * restorable history via Recent. Shown once ever per browser (rule 9's
 * try/catch'd storage, same as every other persisted flag in this app),
 * dismissed by the close button, a click anywhere else, or Escape.
 */
const ONBOARDING_STORAGE_KEY = 'plotter:onboarding-seen';

export function hasSeenOnboarding(): boolean {
  return readStorage(ONBOARDING_STORAGE_KEY) === '1';
}

export function markOnboardingSeen(): void {
  writeStorage(ONBOARDING_STORAGE_KEY, '1');
}

interface OnboardingHintProps {
  onDismiss: () => void;
}

export default function OnboardingHint({ onDismiss }: OnboardingHintProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  return (
    <div
      role="status"
      className="absolute left-3 top-3 z-10 max-w-[15rem] rounded-sm border border-ink-3 bg-ink-2/95 px-3 py-2.5 text-text-ink shadow-lg"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label="Dismiss tip"
        onClick={onDismiss}
        className="absolute right-1.5 top-1.5 rounded-sm p-0.5 text-mute hover:bg-ink-3 hover:text-text-ink"
      >
        <X size={12} aria-hidden />
      </button>
      <p className="pr-4 text-xs leading-relaxed">
        <b>Fit</b> (below) snaps a big diagram back into view. <b>Recent</b> (top bar) keeps every diagram
        you've replaced, titled and restorable.
      </p>
    </div>
  );
}

/** Whether the hint should show at all — first-ever successful render, not shown before. */
export function useOnboardingHint(hasRenderedOnce: boolean): { visible: boolean; dismiss: () => void } {
  const [dismissed, setDismissed] = useState(false);
  const [seenBefore] = useState(hasSeenOnboarding);

  function dismiss() {
    setDismissed(true);
    markOnboardingSeen();
  }

  return { visible: hasRenderedOnce && !seenBefore && !dismissed, dismiss };
}
